import { Button, FieldWrapper, Input, Label } from '@commenda/kakashi';
import {
  FieldRefSource,
  TBaseSubWorkflowStepDefinition,
  TMapping,
  TValueProvider,
  TWorkflowDefinition,
} from '@commenda/types';
import { cn } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidV4 } from 'uuid';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';
import { useForm } from '../../../hooks/useZodForm';
import { ValueProviderEditor } from '../editors/ValueProviderEditor';

const schema = z.object({
  targetPath: z.string().trim().min(1),
  value: z.any(),
});

type TMappingFormFields = z.infer<typeof schema>;

export const MappingForm = ({
  mapping,
  onMappingChange,
  onCancel,
  formContentClassName,
  formFooterClassName,
  workflowDefinition,
  currentStepId,
}: {
  mapping: TMapping | null;
  onMappingChange: (mapping: TMapping) => void;
  onCancel: () => void;
  formContentClassName?: string;
  formFooterClassName?: string;
  workflowDefinition: TWorkflowDefinition;
  currentStepId: string;
}) => {
  const { setNotification } = useNotification();

  const selectedStep = workflowDefinition.steps.find(
    (step) => step.id === currentStepId
  ) as TBaseSubWorkflowStepDefinition;
  const selectedDataSources = selectedStep?.dataSources || [];

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors: formErrors },
  } = useForm<TMappingFormFields>({
    resolver: zodResolver(schema),
    defaultValues: {
      targetPath: mapping?.targetPath,
      value: mapping?.value,
    },
  });

  const onSubmit = (data: TMappingFormFields) => {
    if (!data.value) {
      setNotification({
        title: 'Error',
        message: 'Value is required',
        show: true,
        valid: false,
      });
      return;
    }
    onMappingChange({ ...mapping, ...data, id: mapping?.id || uuidV4() });
  };

  return (
    <div>
      <div className={cn('flex flex-col gap-4', formContentClassName)}>
        <FieldWrapper>
          <Label>Target Path</Label>
          <Input
            value={watch('targetPath')}
            onChange={(e) => setValue('targetPath', e.target.value)}
            error={formErrors?.targetPath?.message}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Label>Value</Label>
          <ValueProviderEditor
            steps={workflowDefinition.steps || []}
            dataSources={selectedDataSources}
            value={watch('value')}
            onChange={(val: TValueProvider) => setValue('value', val)}
            allowedFieldRefSources={[
              FieldRefSource.CONTEXT,
              FieldRefSource.STEP,
              FieldRefSource.DATASOURCE,
            ]}
          />
        </FieldWrapper>
      </div>
      <div
        className={cn('flex gap-3 justify-end border-t ', formFooterClassName)}
      >
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
};
